import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../layout/Layout.js';
import Section from '../../common/Section.js';
import Button from '../../common/Button.js';
import styled from 'styled-components';
import { FaArrowLeft, FaHome, FaMapMarkerAlt } from 'react-icons/fa';

// Hero section for the ForSale page
const Hero = styled.div`
  position: relative;
  background-color: var(--primary);
  color: white;
  padding: 4rem 0;
  margin-bottom: 3rem;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('/images/hero2.png');
    background-size: cover;
    background-position: center;
    opacity: 0.3;
    z-index: 0;
  }
  
  .container {
    position: relative;
    z-index: 1;
  }
  
  h1 {
    font-size: 3rem;
    margin-bottom: 1.5rem;
    font-weight: 700;
  }
  
  p {
    font-size: 1.25rem;
    max-width: 800px;
    margin: 0 auto;
    opacity: 0.9;
  }
  
  @media (max-width: 768px) {
    padding: 3rem 0;
    
    h1 {
      font-size: 2.25rem;
    }
    
    p {
      font-size: 1rem;
    }
  }
`;

// Navigation back link
const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  margin: 1rem 0 2rem;
  color: var(--primary);
  font-weight: 500;
  transition: var(--transition);
  
  svg {
    margin-right: 0.5rem;
  }
  
  &:hover {
    color: var(--secondary);
  }
`;

// Empty state message styling
const EmptyState = styled.div`
  background-color: var(--light);
  border-radius: var(--border-radius-lg);
  padding: 3rem;
  text-align: center;
  max-width: 800px;
  margin: 2rem auto 4rem;
  box-shadow: var(--shadow-sm);
  
  h3 {
    color: var(--primary);
    margin-bottom: 1rem;
    font-size: 1.75rem;
  }
  
  p {
    color: var(--dark);
    font-size: 1.1rem;
    margin-bottom: 1.5rem;
  }
  
  svg {
    font-size: 3rem;
    color: var(--secondary);
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
    margin: 1rem auto 3rem;
    
    h3 {
      font-size: 1.5rem;
    }
    
    p {
      font-size: 1rem;
    }
  }
`;

// Future listing grid
const ListingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

// Placeholder listing card
const ListingCard = styled.div`
  background-color: var(--white);
  border-radius: var(--border-radius-md);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  transition: var(--transition);
  
  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-5px);
  }
  
  .image {
    width: 100%;
    height: 200px;
    background-color: var(--gray);
    position: relative;
    
    .status {
      position: absolute;
      top: 1rem;
      right: 1rem;
      background-color: var(--secondary);
      color: white;
      padding: 0.25rem 0.75rem;
      border-radius: var(--border-radius-sm);
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
    }
  }
  
  .details {
    padding: 1.5rem;
    
    h3 {
      color: var(--primary);
      margin-bottom: 0.5rem;
      font-size: 1.25rem;
      display: flex;
      align-items: center;
      
      svg {
        margin-right: 0.5rem;
        color: var(--secondary);
      }
    }
    
    p {
      color: var(--dark);
      margin-bottom: 1rem;
      font-size: 0.9rem;
      line-height: 1.6;
    }
  }
`;

// Title section for the future listings
const ListingsSectionTitle = styled.div`
  text-align: center;
  margin: 4rem 0 2rem;
  
  h2 {
    color: var(--primary);
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    color: var(--gray);
    max-width: 600px;
    margin: 0 auto;
  }
  
  @media (max-width: 768px) {
    margin: 3rem 0 1.5rem;
    
    h2 {
      font-size: 1.75rem;
    }
  }
`;

const ForSalePage = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <Hero>
        <div className="container">
          <h1>Homes For Sale</h1>
          <p>Explore available Shoffner Homes — thoughtfully built, move-in ready, and located across the NC Triad.</p>
        </div>
      </Hero>
      
      <Section>
        <div className="container">
          {/* Back to Home link removed as requested - navigation bar already has Home link */}
          
          {/* Intro Message */}
          <EmptyState>
            <FaHome />
            <h3>Quality Homes Coming Soon</h3>
            <p>While we don't have any move-in ready homes currently available, we're excited to share our upcoming projects below. Contact us for information on pre-sale opportunities!</p>
            <Button as={Link} to="/contact" variant="secondary">Contact Us Today</Button>
          </EmptyState>
          
          {/* Coming Soon Section */}
          <ListingsSectionTitle>
            <h2>Coming Soon</h2>
            <p>Preview our upcoming home designs available for pre-sale in Guilford County</p>
          </ListingsSectionTitle>
          
          {/* Home Listings Grid */}
          <ListingsGrid>
            {/* H1 – Two-Story Craftsman */}
            <ListingCard>
              <div className="image" style={{ position: 'relative', overflow: 'hidden' }}>
                <img 
                  src="/images/H1.png" 
                  alt="Two-Story Craftsman Home" 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover',
                    position: 'absolute',
                    top: 0,
                    left: 0
                  }} 
                />
                <div className="status">Coming Soon</div>
              </div>
              <div className="details">
                <h3><FaMapMarkerAlt /> Two-Story Craftsman</h3>
                <p style={{ fontWeight: '600', color: 'var(--secondary)', marginBottom: '0.5rem' }}>
                  3 Bed | 2.5 Bath | Guilford County
                </p>
                <p>
                  This elegant two-story home features a wide front porch, clean gray siding, and classic red shutters. With 2,000+/- sq ft of smartly designed living space, it offers modern comforts and curb appeal perfect for a 1-acre lot.
                </p>
                <Button as={Link} to="/contact" variant="outline" fullWidth>Request Information</Button>
              </div>
            </ListingCard>
            
            {/* H2 – Ranch-Style Stone Accent */}
            <ListingCard>
              <div className="image" style={{ position: 'relative', overflow: 'hidden' }}>
                <img 
                  src="/images/H2.png" 
                  alt="Ranch-Style Stone Accent Home" 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover',
                    position: 'absolute',
                    top: 0,
                    left: 0
                  }} 
                />
                <div className="status">Coming Soon</div>
              </div>
              <div className="details">
                <h3><FaMapMarkerAlt /> Ranch-Style Stone Accent</h3>
                <p style={{ fontWeight: '600', color: 'var(--secondary)', marginBottom: '0.5rem' }}>
                  3 Bed | 2.5 Bath | Guilford County
                </p>
                <p>
                  A single-story home with warm stone accents and a welcoming front porch. At 1,950+/- sq ft, it blends timeless style with efficient design — ideal for families seeking quiet, spacious living.
                </p>
                <Button as={Link} to="/contact" variant="outline" fullWidth>Request Information</Button>
              </div>
            </ListingCard>
            
            {/* Additional Available Lot */}
            <ListingCard>
              <div className="image">
                <div className="status">Available Lot</div>
              </div>
              <div className="details">
                <h3><FaMapMarkerAlt /> Custom Build Opportunity</h3>
                <p>
                  Have your own home design in mind? We have premium lots available for custom builds. Work with our team to bring your dream home to life in a desirable Guilford County location.
                </p>
                <Button as={Link} to="/contact" variant="outline" fullWidth>Inquire About Lots</Button>
              </div>
            </ListingCard>
          </ListingsGrid>
        </div>
      </Section>
    </Layout>
  );
};

export default ForSalePage;